import React, { Fragment, useCallback, useMemo, useRef } from "react";
import { Dialog, DialogBackdrop, DialogPanel, Transition } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { requestCloseModal } from "@/redux/actions/modal";
import { MODAL_SIZES, MODAL_COLLECTION } from "@/components/modals/util";

export function Modal() {
  const { isOpen, fragment, options } = useSelector(state => state.modal);
  const dispatch = useDispatch();
  const focus = useRef();

  const handleClose = useCallback(() => {
    dispatch(requestCloseModal());
  }, [dispatch]);

  const ModalInstance = useMemo(() => {
    const Instance = MODAL_COLLECTION[fragment];

    if (!Instance) return null;

    return <Instance handleClose={handleClose} options={options} />;
  }, [fragment, handleClose, options]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-modal" initialFocus={focus} onClose={handleClose}>
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in"
        />
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center px-4 pt-12 pb-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                ref={focus}
                className={classNames(
                  "relative w-full inline-block rounded-md bg-white dark:bg-slate-900 text-left shadow-2xl transform transition-all sm:my-8",
                  { "max-w-2xl": options.size === MODAL_SIZES.SM },
                  { "max-w-4xl": options.size === MODAL_SIZES.MD },
                  { "max-w-5xl": options.size === MODAL_SIZES.LG },
                )}
              >
                {ModalInstance}
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
